import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductLine } from "@/platform/ProductLineProvider";
import {
  PRODUCT_LINES,
  PRODUCT_LINE_HOME,
  productLineLabel,
  type ProductLine,
} from "@/platform/productLine";

// The top-bar product-line switcher — the pill next to the Brand that rotates
// the whole portal between the Commercial and Surety platforms. Picking a line
// just navigates to that platform's home; the active line (and so the left nav
// and this trigger's label) is derived from the route, so the switcher has
// nothing else to set (BRK-S090). Same open/close affordances as the account
// menu (aria-expanded, closes on Escape / outside click / selection).
export function ProductLineSwitcher() {
  const { productLine } = useProductLine();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function choose(line: ProductLine) {
    setOpen(false);
    if (line !== productLine) navigate(PRODUCT_LINE_HOME[line]);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Platform: ${productLineLabel(productLine)}`}
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
      >
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full bg-brk-accent"
        />
        <span>{productLineLabel(productLine)}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 text-white/70 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-40 mt-1 w-52 overflow-hidden rounded-md border border-brk-border bg-brk-panel py-1 shadow-lg"
        >
          {PRODUCT_LINES.map((line) => {
            const active = line.id === productLine;
            return (
              <button
                key={line.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => choose(line.id)}
                className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-brk-bg ${
                  active ? "font-semibold text-brk-accent" : "text-brk-text"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full ${active ? "bg-brk-accent" : "bg-brk-border"}`}
                  />
                  {line.label}
                </span>
                {active && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="text-brk-accent"
                  >
                    <path
                      d="m5 12 5 5 9-11"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
